// BrandFields — the shared brand-colour authoring group (ADR-044 brand identity).
// The LOCKED colour model: the admin picks ONE colour (the brand hue); the ink on
// a brand fill (`color_on_primary`) is auto-derived for max WCAG contrast (DESIGN-TOKENS §Color)
// and shown read-only, with an "Advanced" manual override for the rare tinted-ink
// case. A live preview + contrast meter + advisories make legibility
// visible before save. Reused by the platform project create wizard + edit drawer,
// and (later) the project_admin /settings surface — one component, so the two
// surfaces can't drift.
//
// Why one picker, not two: across the telecom-operator market this app targets,
// many brands' marketing ink (white on a mid-tone red) is sub-AA — a second ink
// picker invites shipping illegible buttons. Deriving the ink guarantees contrast;
// the override stays available but is advised against when it fails.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColorField } from '../ui/ColorField.js';
import { Toggle } from '../ui/Toggle.js';
import { Badge } from '../ui/Badge.js';
import { Alert } from '../ui/Alert.js';
import { contrastRatio, deriveOnPrimary, oklchHC, AA_CONTRAST } from '../../lib/oklch-fallback.js';

export interface BrandValue {
  color_primary: string;
  color_on_primary: string;
}

export interface BrandFieldsProps {
  value: BrandValue;
  onChange: (next: BrandValue) => void;
}

// Danger-hue band in OKLCH degrees (red). Mirrors the Layer-3 --color-danger hue
// so a red-brand project's primary button doesn't read as a danger button
// (DESIGN-TOKENS §Color). Chroma floor skips near-greys (no meaningful hue to clash).
const DANGER_HUE = 28;
const HUE_TOL = 18;
const CHROMA_FLOOR = 0.09;

type Tier = 'aaa' | 'aa' | 'tight' | 'fail';
function tierFor(ratio: number): Tier {
  if (ratio >= 7) return 'aaa';
  if (ratio >= AA_CONTRAST) return 'aa';
  if (ratio >= 3) return 'tight';
  return 'fail';
}

export function BrandFields({ value, onChange }: BrandFieldsProps) {
  const { t } = useTranslation('common');
  const [manual, setManual] = useState(false);

  const derived = deriveOnPrimary(value.color_primary);
  const ink = manual ? value.color_on_primary : derived;
  const ratio = contrastRatio(value.color_primary, ink);
  const tier = tierFor(ratio);

  const { h, c } = oklchHC(value.color_primary);
  const hueDist = Math.min(Math.abs(h - DANGER_HUE), 360 - Math.abs(h - DANGER_HUE));
  const nearDanger = c >= CHROMA_FLOOR && hueDist <= HUE_TOL;

  const setPrimary = (hex: string) =>
    onChange({ color_primary: hex, color_on_primary: manual ? value.color_on_primary : deriveOnPrimary(hex) });

  const toggleManual = (on: boolean) => {
    setManual(on);
    // Leaving manual mode → snap the ink back to the derived value.
    if (!on) onChange({ ...value, color_on_primary: deriveOnPrimary(value.color_primary) });
  };

  const badgeVariant = tier === 'fail' ? 'danger' : tier === 'tight' ? 'warning' : 'success';

  return (
    <div className="flex flex-col gap-4">
      <ColorField
        label={t('brandFields.primaryLabel')}
        help={t('brandFields.primaryHelp')}
        value={value.color_primary}
        onChange={setPrimary}
      />

      {/* Live preview — the brand fill with the effective ink, plus the contrast meter. */}
      <div>
        <div className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">{t('brandFields.preview')}</div>
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface-alt)] p-3">
          <span
            className="inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium"
            style={{ backgroundColor: value.color_primary, color: ink }}
          >
            {t('brandFields.sampleButton')}
          </span>
          {/* No brand-coloured "link" sample: links are semantic-coloured, never brand
              (COMPONENTS §Button), and brand text on the page bg fails contrast in dark
              mode. The brand fill + derived ink above + the meter carry legibility. */}
          <Badge variant={badgeVariant} appearance="subtle">
            {`${t(`brandFields.tier.${tier}`)} · ${ratio.toFixed(2)}:1`}
          </Badge>
        </div>
        {!manual && <p className="mt-1 text-xs text-[var(--color-text-secondary)]">{t('brandFields.inkAutoNote')}</p>}
      </div>

      {/* Advanced — manual ink override (rare tinted-ink case). */}
      <Toggle checked={manual} onChange={toggleManual} label={t('brandFields.manualToggle')} />
      {manual && (
        <ColorField
          label={t('brandFields.inkLabel')}
          value={value.color_on_primary}
          onChange={(hex) => onChange({ ...value, color_on_primary: hex })}
        />
      )}

      {/* Advisories — never block (CONVENTIONS: designed states, not narration). */}
      {tier === 'fail' && <Alert variant="warning">{t('brandFields.advisoryContrast')}</Alert>}
      {nearDanger && <Alert variant="warning">{t('brandFields.advisorySemantic')}</Alert>}
    </div>
  );
}
